/**
 * Fonctions de calcul pour la calculatrice AEP
 * ELECTRON INDUSTRIE - Bureau d'études Industrie Mécanique
 * 
 * Formules:
 * - Pression exploitable = max(Pression amont - 3, 0)
 * - Si débit en m³/h : P(W) = 27.78 × pression exploitable × débit
 * - Si débit en L/min : P(W) = 1.667 × pression exploitable × débit
 * - Puissance utile : P utile = P théorique × rendement
 * - Energie : E(kWh) = P(kW) × durée(h)
 */

import { 
  PRESSION_AVAL_MIN, 
  TARIFS, 
  COEFF_M3H, 
  COEFF_LMIN 
} from './aep-constants';

/**
 * Calcule la pression exploitable
 * @param {number} pressionAmont - Pression amont en bars
 * @returns {number} Pression exploitable en bars (minimum 0)
 */
export const calculerPressionExploitable = (pressionAmont) => {
  return Math.max(pressionAmont - PRESSION_AVAL_MIN, 0);
};

/**
 * Calcule la puissance théorique en Watts
 * @param {number} pressionExploitable - Pression exploitable en bars
 * @param {number} debit - Débit
 * @param {string} unite - Unité de débit ('lmin' ou 'm3h')
 * @returns {number} Puissance théorique en W
 */
export const calculerPuissanceTheorique = (pressionExploitable, debit, unite) => {
  if (pressionExploitable <= 0) return 0;
  
  const coefficient = unite === 'm3h' ? COEFF_M3H : COEFF_LMIN;
  return coefficient * pressionExploitable * debit;
};

/**
 * Calcule la puissance utile en Watts
 * @param {number} puissanceTheorique - Puissance théorique en W
 * @param {number} rendement - Rendement en pourcentage
 * @returns {number} Puissance utile en W
 */
export const calculerPuissanceUtile = (puissanceTheorique, rendement) => {
  return puissanceTheorique * (rendement / 100);
};

/**
 * Calcule l'énergie en kWh
 * @param {number} puissanceKW - Puissance en kW
 * @param {number} heures - Durée en heures
 * @returns {number} Énergie en kWh
 */
export const calculerEnergie = (puissanceKW, heures) => {
  return puissanceKW * heures;
};

/**
 * Calcule les revenus potentiels
 * @param {number} energieKWh - Énergie en kWh
 * @returns {object} Revenus vente EDF et autoconsommation
 */
export const calculerRevenus = (energieKWh) => {
  return {
    venteEDF: energieKWh * TARIFS.venteEDF,
    autoconsommation: energieKWh * TARIFS.autoconsommation
  };
};

/**
 * Calcule tous les résultats pour une combinaison de paramètres
 * @param {object} params - Paramètres de calcul
 * @returns {object} Résultats complets
 */
export const calculerResultatsComplets = ({ 
  pressionAmont, 
  debit, 
  unite, 
  rendement, 
  dureeHeures 
}) => {
  const pressionExploitable = calculerPressionExploitable(pressionAmont);
  const puissanceTheoriqueW = calculerPuissanceTheorique(pressionExploitable, debit, unite);
  const puissanceTheoriqueKW = puissanceTheoriqueW / 1000;
  const puissanceUtileW = calculerPuissanceUtile(puissanceTheoriqueW, rendement);
  const puissanceUtileKW = puissanceUtileW / 1000;
  const energieTheoriqueKWh = calculerEnergie(puissanceTheoriqueKW, dureeHeures);
  const energieUtileKWh = calculerEnergie(puissanceUtileKW, dureeHeures);
  const revenus = calculerRevenus(energieUtileKWh);

  return {
    pressionAmont,
    pressionAval: PRESSION_AVAL_MIN,
    pressionExploitable,
    puissanceTheoriqueW,
    puissanceTheoriqueKW,
    puissanceUtileW,
    puissanceUtileKW,
    energieTheoriqueKWh,
    energieUtileKWh,
    revenusVenteEDF: revenus.venteEDF,
    revenusAutoconsommation: revenus.autoconsommation
  };
};

/**
 * Formate un nombre avec séparateur de milliers (format français)
 * @param {number} value - Valeur à formater
 * @param {number} decimals - Nombre de décimales
 * @returns {string} Valeur formatée
 */
export const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Formate un prix en euros
 * @param {number} value - Valeur en euros
 * @returns {string} Prix formaté
 */
export const formatPrice = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};
